/**
 * Bizon 2.0 — 4Vision AI Search (dialog-connected)
 *
 * Works with the native <dialog> overlay in site-header.liquid.
 * Uses hidden attribute (not CSS classes) for section visibility.
 * Cloud Function (Gemini) + Shopify suggest fallback.
 * Service-first: prioritizes pages/solutions over products.
 *
 * Config (from inline <script> in site-header.liquid):
 *   window.__INTENT_CONFIG  — { CF_URL, shopDomain, mode }
 *   window.__INTENT_MAP     — local intent keyword → expanded query
 *   window.__INTENT_TO_URL  — intent keyword → page URL
 */
(() => {
    "use strict";

    const dialog = document.getElementById("intentDialog");
    if (!dialog) return;

    const input = document.getElementById("intentSearchInput");
    const panel = document.getElementById("intentPanel");
    const spin = document.getElementById("intentSpinner");

    /* AI insight banner */
    const insight = document.getElementById("intentInsight");
    const insightT = document.getElementById("intentInsightText");

    /* Section containers */
    const sections = {
        pages: { el: document.getElementById("intentPages"), grid: document.getElementById("intentPagesGrid"), count: document.getElementById("intentPagesCount") },
        coll: { el: document.getElementById("intentCollections"), grid: document.getElementById("intentCollGrid"), count: document.getElementById("intentCollCount") },
        products: { el: document.getElementById("intentProducts"), grid: document.getElementById("intentProductsGrid"), count: document.getElementById("intentProductsCount") },
        articles: { el: document.getElementById("intentArticles"), grid: document.getElementById("intentArticlesGrid"), count: document.getElementById("intentArticlesCount") },
    };
    const showAll = document.getElementById("intentShowAll");

    const CFG = window.__INTENT_CONFIG || {};
    const MAP = window.__INTENT_MAP || {};
    const URLS = window.__INTENT_TO_URL || {};

    let timer = 0;
    let lastQuery = "";

    /* ============================================================
       VISIBILITY — uses the hidden attribute (no CSS classes)
       ============================================================ */
    const show = (el) => { if (el) el.hidden = false; };
    const hide = (el) => { if (el) el.hidden = true; };

    function hideAll() {
        [insight, showAll].forEach(hide);
        for (const s of Object.values(sections)) hide(s.el);
    }

    /* ============================================================
       SPINNER — uses aria-busy as the toggling mechanism
       ============================================================ */
    const spinOn = () => { if (spin) spin.setAttribute("aria-busy", "true"); };
    const spinOff = () => { if (spin) spin.removeAttribute("aria-busy"); };

    /* ============================================================
       DIALOG LIFECYCLE — backdrop click closes
       ============================================================ */
    dialog.addEventListener("click", (e) => {
        /* Clicking the backdrop (::backdrop hits the dialog itself when
           the click is outside the dialog's box) */
        if (e.target === dialog) dialog.close();
    });

    dialog.addEventListener("close", () => {
        /* Reset state when dialog is dismissed */
        if (input) input.value = "";
        hideAll();
        spinOff();
        lastQuery = "";
    });

    /* Ctrl+K / Cmd+K keyboard shortcut */
    document.addEventListener("keydown", (e) => {
        if ((e.ctrlKey || e.metaKey) && e.key === "k") {
            e.preventDefault();
            if (!dialog.open) {
                dialog.showModal();
                input?.focus();
            }
        }
    });

    /* ============================================================
       RESULT CARD BUILDERS
       ============================================================ */
    function buildResultCard({ url, title, vendor, snippet, tag, image, icon, price }) {
        const a = document.createElement("a");
        a.className = "intent-search__result-card";
        a.href = url || "#";

        if (image) {
            const img = document.createElement("img");
            img.className = "intent-search__result-thumb";
            img.src = image.startsWith("//") ? `https:${image}` : image;
            img.alt = title || "";
            img.loading = "lazy";
            img.width = 48;
            img.height = 48;
            a.appendChild(img);
        } else if (icon) {
            const iconEl = document.createElement("span");
            iconEl.className = "intent-search__result-thumb";
            iconEl.textContent = icon;
            iconEl.style.cssText = "display:flex;align-items:center;justify-content:center;font-size:24px;background:rgba(255,255,255,0.04);";
            a.appendChild(iconEl);
        }

        const info = document.createElement("div");
        info.className = "intent-search__result-info";

        if (vendor || tag) {
            const vendorEl = document.createElement("div");
            vendorEl.className = "intent-search__result-vendor";
            vendorEl.textContent = vendor || tag || "";
            info.appendChild(vendorEl);
        }

        const titleEl = document.createElement("div");
        titleEl.className = "intent-search__result-title";
        titleEl.textContent = title || "";
        info.appendChild(titleEl);

        if (snippet) {
            const snipEl = document.createElement("div");
            snipEl.className = "intent-search__result-title";
            snipEl.style.cssText = "color:rgba(255,255,255,0.4);font-size:11px;-webkit-line-clamp:1;";
            snipEl.textContent = snippet;
            info.appendChild(snipEl);
        }

        a.appendChild(info);

        if (price) {
            const priceEl = document.createElement("div");
            priceEl.className = "intent-search__result-price";
            priceEl.textContent = price;
            a.appendChild(priceEl);
        }

        return a;
    }

    /* ============================================================
       LOCAL INTENT MAPPING
       ============================================================ */
    function localIntentLookup(query) {
        const q = query.toLowerCase();
        for (const [key, val] of Object.entries(MAP)) {
            if (q.includes(key)) return val;
        }
        return null;
    }

    function findIntentURL(query) {
        const q = query.toLowerCase();
        for (const [key, url] of Object.entries(URLS)) {
            if (q.includes(key)) return url;
        }
        return null;
    }

    /* ============================================================
       STRIP HTML (for page/article body snippets)
       ============================================================ */
    function stripHtml(html) {
        if (!html) return "";
        return html
            .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, "")
            .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, "")
            .replace(/<[^>]*>/g, "")
            .replace(/&nbsp;/g, " ")
            .replace(/&amp;/g, "&")
            .replace(/\s+/g, " ")
            .trim();
    }

    /* ============================================================
       SHOPIFY SEARCH API (universal fallback)
       ============================================================ */
    async function shopifySearch(query) {
        const domain = CFG.shopDomain || "";
        const url = `${domain}/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product,page,article,collection&resources[limit]=6`;

        const r = await fetch(url);
        const data = await r.json();
        const res = data.resources?.results || {};

        return {
            products: (res.products || []).map((p) => ({
                title: p.title,
                url: p.url,
                image: p.image || p.featured_image?.url || "",
                vendor: p.vendor || "",
                price: p.price ? `${(parseFloat(p.price) / 100).toFixed(2)} zł` : "",
            })),
            pages: (res.pages || []).map((p) => ({
                title: p.title,
                url: p.url,
                snippet: stripHtml(p.body)?.substring(0, 100) || "",
                tag: p.url?.includes("intent-page") ? "🎯 Intent" : "Strona",
                icon: p.url?.includes("intent-page") ? "🎯" : "📄",
            })),
            articles: (res.articles || []).map((a) => ({
                title: a.title,
                url: a.url,
                snippet: stripHtml(a.body)?.substring(0, 100) || "",
                tag: "Blog",
                icon: "📝",
                image: a.image || a.featured_image?.url || "",
            })),
            collections: (res.collections || []).map((c) => ({
                title: c.title,
                url: c.url,
                tag: "Kategoria",
                icon: "📂",
                image: c.featured_image?.url || "",
            })),
        };
    }

    /* ============================================================
       CLOUD FUNCTION (Gemini) SEARCH
       ============================================================ */
    async function cloudSearch(query) {
        if (!CFG.CF_URL) throw new Error("No CF URL configured");

        const r = await fetch(CFG.CF_URL, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ query }),
        });

        if (!r.ok) throw new Error(`CF ${r.status}`);
        const data = await r.json();
        if (data.error) throw new Error(data.error);
        return data;
    }

    /* ============================================================
       RENDER RESULTS INTO DIALOG
       ============================================================ */
    function renderResults(data) {
        hideAll();

        /* AI Insight */
        if (data.intent?.intent) {
            insightT.textContent = `Rozumiemy: ${data.intent.intent}`;
            show(insight);
        }

        /* Pages / Solutions (primary) */
        const pages = data.pages || [];
        if (pages.length) {
            sections.pages.grid.replaceChildren(...pages.map((p) => buildResultCard(p)));
            sections.pages.count.textContent = `${pages.length}`;
            show(sections.pages.el);
        }

        /* Collections */
        const colls = data.collections || [];
        if (colls.length) {
            sections.coll.grid.replaceChildren(...colls.map((c) => buildResultCard(c)));
            sections.coll.count.textContent = `${colls.length}`;
            show(sections.coll.el);
        }

        /* Products (secondary) */
        const prods = data.products || [];
        if (prods.length) {
            sections.products.grid.replaceChildren(...prods.map((p) => buildResultCard(p)));
            sections.products.count.textContent = `${prods.length}`;
            show(sections.products.el);
        }

        /* Articles */
        const articles = data.articles || [];
        if (articles.length) {
            sections.articles.grid.replaceChildren(...articles.map((a) => buildResultCard(a)));
            sections.articles.count.textContent = `${articles.length}`;
            show(sections.articles.el);
        }

        /* Show all link */
        const intentURL = findIntentURL(data.query || "");
        if (intentURL && showAll) {
            showAll.href = intentURL;
            show(showAll);
        } else if (showAll && (pages.length || prods.length)) {
            showAll.href = `/search?q=${encodeURIComponent(data.query || "")}`;
            show(showAll);
        }
    }

    /* ============================================================
       MAIN SEARCH (CF → Shopify fallback pipeline)
       ============================================================ */
    async function doSearch(query) {
        if (query.length < 2 || query === lastQuery) return;
        lastQuery = query;
        spinOn();
        hideAll();

        try {
            let results;

            if (CFG.CF_URL) {
                /* Strategy: CF first, then enrich with Shopify for pages/articles */
                try {
                    const cf = await cloudSearch(query);
                    const shopify = await shopifySearch(cf.intent?.search_query || query);
                    results = {
                        query,
                        intent: cf.intent || null,
                        products: shopify.products,
                        pages: shopify.pages,
                        articles: shopify.articles,
                        collections: shopify.collections,
                    };
                } catch {
                    /* CF failed → pure Shopify */
                    const shopify = await shopifySearch(query);
                    results = { query, ...shopify };
                }
            } else {
                /* No CF configured → local intent + Shopify */
                const mapped = localIntentLookup(query) || query;
                const shopify = await shopifySearch(mapped);
                results = { query, ...shopify };
                if (mapped !== query) {
                    results.intent = { intent: `Szukasz: ${mapped}` };
                }
            }

            renderResults(results);
        } catch {
            /* Total failure — silently close */
            hideAll();
        } finally {
            spinOff();
        }
    }

    /* ============================================================
       INPUT LISTENER (debounced)
       ============================================================ */
    input?.addEventListener("input", () => {
        clearTimeout(timer);
        const q = input.value.trim();

        if (q.length < 2) {
            hideAll();
            lastQuery = "";
            return;
        }

        timer = setTimeout(() => doSearch(q), 350);
    });

    /* Submit on Enter */
    input?.addEventListener("keydown", (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            clearTimeout(timer);
            const q = input.value.trim();
            if (q.length >= 2) doSearch(q);
        }
    });

    /* Close dialog on result-card click (navigate) */
    panel?.addEventListener("click", (e) => {
        if (e.target.closest(".intent-search__result-card")) {
            dialog.close();
        }
    });
})();
